// src/services/paymentService.js

import * as api from './api';

/**
 * API service for handling payment-related operations
 * with signature throttling
 */

// Store the last valid signature data
const signatureCache = {
  message: null,
  signature: null,
  timestamp: 0,
  walletAddress: null,
  networkId: null,
  walletType: null
};

/**
 * Helper function to convert network name to network ID
 * 
 * @param {string|number} networkIdentifier - Network name or ID
 * @returns {number} - Network ID as a number
 */
const getNetworkId = (networkIdentifier) => {
  // If it's already a number, return it
  if (!isNaN(parseInt(networkIdentifier))) {
    return parseInt(networkIdentifier);
  }
  
  // Map network names to IDs
  const networkMap = {
    'ethereum': 1,
    'bsc': 2,
    'solana': 3,
    'polygon': 4
  };
  
  // Convert to lowercase to ensure matching
  const networkName = String(networkIdentifier).toLowerCase();
  
  // Return mapped ID or default to 1 (ethereum)
  return networkMap[networkName] || 1;
};

/**
 * Clean amount value for API requests
 * Remove any formatting like commas or currency symbols
 * 
 * @param {string|number} amount - Amount value that might be formatted
 * @returns {string} - Cleaned amount as a string without formatting
 */
const cleanAmount = (amount) => {
  if (typeof amount === 'number') {
    return amount.toString();
  }
  
  if (typeof amount === 'string') {
    // Remove any commas, currency symbols, and other non-numeric chars except decimal point
    return amount.replace(/[^0-9.]/g, '');
  }
  
  return '0';
};


/**
 * Get a valid signature with throttling
 * 
 * @param {string} walletAddress - User's wallet address
 * @param {string|number} blockchainNetworkId - Blockchain network ID or name
 * @param {string} walletType - Type of wallet
 * @param {boolean} forceNew - Force a new signature even if cached one is valid
 * @returns {Promise<Object>} Response with signature data
 */
async function getValidSignature(walletAddress, blockchainNetworkId, walletType, forceNew = false) {
  const networkId = getNetworkId(blockchainNetworkId);
  const currentTime = Date.now();
  
  // Check if we have a valid cached signature that matches the current request
  if (!forceNew && 
      signatureCache.message && 
      signatureCache.signature && 
      signatureCache.walletAddress === walletAddress &&
      signatureCache.networkId === networkId &&
      signatureCache.walletType === walletType &&
      (currentTime - signatureCache.timestamp) < 60000) { // Valid for 60 seconds
    
    // Return cached signature data
    console.log('Using cached signature from', new Date(signatureCache.timestamp).toLocaleTimeString());
    return {
      signature: signatureCache.signature,
      message: signatureCache.message,
      cached: true
    };
  }
  
  // We need a new signature
  console.log('Requesting new signature at', new Date().toLocaleTimeString());
  
  // Get signature message
  const messageResponse = await api.generateSignatureMessage(
    walletAddress,
    networkId
  );
  
  if (!messageResponse || !messageResponse.message) {
    throw new Error('Invalid signature message response');
  }
  
  // Sign message
  const signature = await api.signMessage(
    walletType,
    walletAddress,
    messageResponse.message
  );
  
  if (!signature) {
    throw new Error('Failed to sign message');
  }
  
  // Update cache
  signatureCache.message = messageResponse.message;
  signatureCache.signature = signature;
  signatureCache.timestamp = currentTime;
  signatureCache.walletAddress = walletAddress;
  signatureCache.networkId = networkId;
  signatureCache.walletType = walletType;
  
  return {
    signature,
    message: messageResponse.message,
    cached: false
  };
}

/**
 * Create a payment order with signature throttling
 * 
 * @param {string} walletAddress - User's wallet address
 * @param {string|number} blockchainNetworkId - Blockchain network ID or name
 * @param {string} walletType - Type of wallet
 * @param {string} productId - ID of the product being purchased
 * @param {string} productName - Name of the product
 * @param {string} amount - Payment amount
 * @param {string|number} selectedNodeId - ID of the selected node
 * @param {string} selectedNetwork - Selected payment network ('bsc' or 'solana')
 * @returns {Promise<Object>} Response with payment order details
 */
export const createPaymentOrder = async (
  walletAddress,
  blockchainNetworkId,
  walletType,
  productId,
  productName,
  amount,
  selectedNodeId,
  selectedNetwork
) => {
  try {
    // Convert network identifier to numeric ID
    const networkId = getNetworkId(blockchainNetworkId);
    
    // Clean the amount to ensure it doesn't have formatting
    const cleanedAmount = cleanAmount(amount);
    
    // Get valid signature with throttling
    const signatureData = await getValidSignature(
      walletAddress, 
      networkId, 
      walletType
    );
    
    // Create payment order
    const response = await fetch('https://api.aeronyx.network/api/payments/create/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        wallet_address: walletAddress,
        blockchain_network_id: networkId,
        signature: signatureData.signature,
        message: signatureData.message,
        wallet_type: walletType,
        product_id: productId,
        product_name: productName,
        amount: cleanedAmount,
        selected_node_id: selectedNodeId,
        selected_network: selectedNetwork
      }),
    });
    
    const result = await response.json();
    
    if (!result.success) {
      // If error is related to invalid/expired signature, try again with a fresh signature
      if (!signatureData.cached && 
          result.message && 
          (result.message.includes('signature') || result.message.includes('auth'))) {
        console.warn('Signature issue detected, forcing new signature');
        
        // Clear signature cache
        signatureCache.message = null;
        signatureCache.signature = null;
        
        // Retry with forced new signature
        return createPaymentOrder(
          walletAddress,
          blockchainNetworkId,
          walletType,
          productId,
          productName,
          amount,
          selectedNodeId,
          selectedNetwork
        );
      }
      
      throw new Error(result.message || 'Failed to create payment order');
    }
    
    return {
      success: true,
      data: result.data
    };
  } catch (error) {
    console.error('Error creating payment order:', error);
    return {
      success: false,
      message: error.message || 'Failed to create payment order'
    };
  }
};

/**
 * Get payment order details with signature throttling
 * 
 * @param {string} orderId - Order ID
 * @param {string} walletAddress - User's wallet address
 * @param {string|number} blockchainNetworkId - Blockchain network ID or name
 * @param {string} walletType - Type of wallet
 * @returns {Promise<Object>} Response with payment order details
 */
export const getPaymentOrderDetails = async (
  orderId,
  walletAddress,
  blockchainNetworkId,
  walletType
) => {
  try {
    // Convert network identifier to numeric ID
    const networkId = getNetworkId(blockchainNetworkId);
    
    // Get valid signature with throttling
    const signatureData = await getValidSignature(
      walletAddress, 
      networkId, 
      walletType
    );
    
    // Get payment order details
    const response = await fetch('https://api.aeronyx.network/api/payments/details/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        order_id: orderId,
        wallet_address: walletAddress,
        blockchain_network_id: networkId,
        signature: signatureData.signature,
        message: signatureData.message,
        wallet_type: walletType
      }),
    });
    
    const result = await response.json();
    
    if (!result.success) {
      // If error is related to invalid/expired signature, try again with a fresh signature
      if (!signatureData.cached && 
          result.message && 
          (result.message.includes('signature') || result.message.includes('auth'))) {
        console.warn('Signature issue detected, forcing new signature');
        
        // Clear signature cache
        signatureCache.message = null;
        signatureCache.signature = null;
        
        // Retry with forced new signature
        return getPaymentOrderDetails(
          orderId,
          walletAddress,
          blockchainNetworkId,
          walletType
        );
      }
      
      throw new Error(result.message || 'Failed to get payment order details');
    }
    
    return {
      success: true,
      data: result.data
    };
  } catch (error) {
    console.error('Error getting payment order details:', error);
    return {
      success: false,
      message: error.message || 'Failed to get payment order details'
    };
  }
};

/**
 * Check payment status with signature throttling
 * 
 * @param {string} orderId - Order ID
 * @param {string} walletAddress - User's wallet address
 * @param {string|number} blockchainNetworkId - Blockchain network ID or name
 * @param {string} walletType - Type of wallet
 * @returns {Promise<Object>} Response with payment status
 */
export const checkPaymentStatus = async (
  orderId,
  walletAddress,
  blockchainNetworkId,
  walletType
) => {
  try {
    // Convert network identifier to numeric ID
    const networkId = getNetworkId(blockchainNetworkId);
    
    // Get valid signature with throttling
    const signatureData = await getValidSignature(
      walletAddress, 
      networkId, 
      walletType
    );
    
    // Check payment status
    const response = await fetch('https://api.aeronyx.network/api/payments/status/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        order_id: orderId,
        wallet_address: walletAddress,
        blockchain_network_id: networkId,
        signature: signatureData.signature,
        message: signatureData.message,
        wallet_type: walletType
      }),
    });
    
    const result = await response.json();
    
    if (!result.success) {
      // If error is related to invalid/expired signature, try again with a fresh signature
      if (!signatureData.cached && 
          result.message && 
          (result.message.includes('signature') || result.message.includes('auth'))) {
        console.warn('Signature issue detected, forcing new signature');
        
        // Clear signature cache
        signatureCache.message = null;
        signatureCache.signature = null;
        
        // Retry with forced new signature
        return checkPaymentStatus(
          orderId,
          walletAddress,
          blockchainNetworkId,
          walletType
        );
      }
      
      throw new Error(result.message || 'Failed to check payment status');
    }
    
    return {
      success: true,
      data: result.data
    };
  } catch (error) {
    console.error('Error checking payment status:', error);
    return {
      success: false,
      message: error.message || 'Failed to check payment status'
    };
  }
};

/**
 * Get list of payment orders for a user with signature throttling
 * 
 * @param {string} walletAddress - User's wallet address
 * @param {string|number} blockchainNetworkId - Blockchain network ID or name
 * @param {string} walletType - Type of wallet
 * @returns {Promise<Object>} Response with list of payment orders
 */
export const getUserPaymentOrders = async (
  walletAddress,
  blockchainNetworkId,
  walletType
) => {
  try {
    // Convert network identifier to numeric ID
    const networkId = getNetworkId(blockchainNetworkId);
    
    // Get valid signature with throttling
    const signatureData = await getValidSignature(
      walletAddress, 
      networkId, 
      walletType
    );
    
    // Get user payment orders
    const response = await fetch('https://api.aeronyx.network/api/payments/list/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        wallet_address: walletAddress,
        blockchain_network_id: networkId,
        signature: signatureData.signature,
        message: signatureData.message,
        wallet_type: walletType
      }),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch payment orders: ${response.status} ${response.statusText}`);
    }
    
    const result = await response.json();
    
    if (!result.success) {
      // If error is related to invalid/expired signature, try again with a fresh signature
      if (!signatureData.cached && 
          result.message && 
          (result.message.includes('signature') || result.message.includes('auth'))) {
        console.warn('Signature issue detected, forcing new signature');
        
        // Clear signature cache
        signatureCache.message = null;
        signatureCache.signature = null;
        
        // Retry with forced new signature
        return getUserPaymentOrders(
          walletAddress,
          blockchainNetworkId,
          walletType
        );
      }
      
      throw new Error(result.message || 'Failed to get user payment orders');
    }
    
    return {
      success: true,
      data: result.data || { orders: [] }
    };
  } catch (error) {
    console.error('Error getting user payment orders:', error);
    return {
      success: false,
      message: error.message || 'Failed to get user payment orders',
      data: { orders: [] }
    };
  }
};

export default {
  createPaymentOrder,
  getPaymentOrderDetails,
  checkPaymentStatus,
  getUserPaymentOrders
};
